import React, { useState, useEffect } from 'react';
import moment from 'moment-timezone';
import CargomaticAlert from 'components/ui/Alert';
import Badge from 'components/v2/Badge';
import Button from 'components/v2/buttons/Button';
import Checkbox from 'components/v2/forms/Checkbox';
import CollapsibleItem from 'components/v2/list/CollapsibleItem';
import LayoutWithSidebar from 'components/v2/LayoutWithSidebar';
import Panel from 'components/v2/Panel';
import PanelSection from 'components/v2/PanelSection';
import ToggleSwitch from 'components/v2/ToggleSwitch';
import CargomaticModal from 'components/v2/modal';
import formatMoney from 'utils/formatters/Money';
import { sluggify } from 'utils/localization/utils';
import { cloneDeep, uniqBy, groupBy } from 'lodash';
import { generateInvoicesByGrouping, validateInvoiceChargesByGrouping } from 'utils/AccountingWebAPIUtils';
import { showToast } from 'stores/ToastStore';
import Label from 'components/v2/forms/Label';
import { INVOICE_GROUPING_TYPES } from 'constants/InvoiceGroupingTypes';
import LoadingCircle from 'components/ui/LoadingCircle';
import SessionStore from 'stores/SessionStore';

const FUEL_SURCHARGE_TYPES = ['fuel_surcharge', 'fuel_surcharge_bobtail', 'fuel_surcharge_linehaul'];

const INVOICE_GROUPINGS = {
  ONE_PER_SHIPMENT: {
    ALL: {
      charges: [],
      groupedChargeTypes: []
    },
  },
  ONE_PER_SHIPMENT_PER_CHARGE_TYPE: {
    SHIPPER_COST: {
      charges: [],
      groupedChargeTypes: []
    },
    FUEL_SURCHARGE: {
      charges: [],
      groupedChargeTypes: []
    },
    ALL_BUT_LINE_HAUL_AND_FUEL_SURCHARGE: {
      charges: [],
      groupedChargeTypes: []
    },
  },
  TWO_PER_SHIPMENT: {
    CHASSIS_AND_PER_DIEM: {
      charges: [],
      groupedChargeTypes: []
    },
    ALL_BUT_CHASSIS_AND_PER_DIEM: {
      charges: [],
      groupedChargeTypes: []
    },
  },
  UNLIMITED: {
    SHIPPER_COST: {
      charges: [],
      groupedChargeTypes: []
    },
    FUEL_SURCHARGE: {
      charges: [],
      groupedChargeTypes: []
    },
    ALL_BUT_LINE_HAUL_AND_FUEL_SURCHARGE: {
      charges: [],
      groupedChargeTypes: []
    },
  },
}

export default function InvoiceGroupingModal({ show, orderId, equipment, shipper, carrier, shipments, ledgerType, onClose, t, useShipmentAddress, invoiceSettings }) {
  const [hasRetrievedSettings, setHasRetrievedSettings] = useState(false);
  const [invoiceGrouping, setInvoiceGrouping] = useState('UNLIMITED');
  const [groupedCharges, setGroupedCharges] = useState([]);
  const [groupAll, setGroupAll] = useState(false);
  const [invoiceSuccesses, setInvoiceSuccesses] = useState([]);
  const [invoiceErrors, setInvoiceErrors] = useState([]);
  const [lockInvoicing, setLockInvoicing] = useState(false);
  const [isValidatingGroupedCharges, setIsValidatingGroupedCharges] = useState(false);
  const [areValidGroupings, setAreValidGroupings] = useState(false);
  const [validCharges, setValidCharges] = useState([]);
  const [invalidCharges, setInvalidCharges] = useState([]);
  const [showValidationAlert, setShowValidationAlert] = useState(true);

  const getInvoiceSettings =  () => {
      if (invoiceSettings?.invoice_grouping) setInvoiceGrouping(invoiceSettings?.invoice_grouping);
      setHasRetrievedSettings(true);

  };

  useEffect(() => {
    getInvoiceSettings();
    if (shipper?.group_like_invoice_items) setGroupAll(true);
  }, [shipper]);

  useEffect(() => {
    validateSelectedGroupedCharges();
  }, [groupedCharges, invoiceGrouping]);

  useEffect(() => {
    if (hasRetrievedSettings) {
      let reducedShipments = shipments.reduce((acc, shipment) => {
        acc.push(sortCharges(invoiceGrouping, shipment));
        return acc;
      }, []);
      setGroupedCharges(reducedShipments);
    }
  }, [hasRetrievedSettings, groupAll]);

  const isInvoiceButtonDisabled = () => {
    const canInvoice = (
      !lockInvoicing &&
      !isValidatingGroupedCharges &&
      areValidGroupings
    );
    return !canInvoice;
  };

  const noDeliveryInvoiceButton = () => {
    if (!SessionStore.isBillingManagerAdmin()) {
      return null;
    }

    return (
      <Button
        color="green"
        className="mr-2"
        onClick={() => generateInvoices({noDeliveryInvoice: true})}
        disabled={isInvoiceButtonDisabled()}
      >
        {t('GENERATE_NO_DELIVERY_INVOICE')}
      </Button>
    )
  };

  const generateInvoices = async (options) => {
    try {
      setLockInvoicing(true);
      const { noDeliveryInvoice = false } = options;
      const data = mapDataPayload(groupedCharges)
      const selectedCharges = data.map((charge) => {
        let  { SHIPPER_COST, ...remainder } = charge.grouping
        charge.grouping = { ...remainder, LINE_HAUL: SHIPPER_COST };
        return charge;
      })

      const payload = {
        ledgerDate: moment.tz(DEFAULT_TIMEZONE).toISOString(),
        selectedCharges,
        useShipmentAddress,
        noDeliveryInvoice,
      };

      const response = await generateInvoicesByGrouping(orderId, payload);
      setLockInvoicing(false);
      const successes = response?.data?.results?.successes || [];
      const errors = response?.data?.results?.errors || [];
      setInvoiceSuccesses(successes);
      setInvoiceErrors(errors);
      if (!errors.length && successes.length) {
        showToast(t('INVOICE_GENERATION_SUCCESS'));
        onClose();
        return
      }
      if (errors.length && successes.length) {
        showToast(t('INVOICE_GENERATE_MIXED_SUCCESS'));
        return;
      }
      showToast(t('INVOICE_GENERATION_FAILURE', null, 'error'));
    } catch (e) {
      console.error(`Error generating invoice: ${e}`);
      showToast(t('INVOICE_GENERATION_FAILURE'), null, 'error');
      setLockInvoicing(false);
    }
  }

  const getShipmentGroupCharges = (shipmentId, group) => {
    const shipmentGroup = groupedCharges.find((shipmentGrouping) => shipmentGrouping?.shipmentId === shipmentId);
    if (!shipmentGroup) {
      return [];
    }
    const chargeGroup = shipmentGroup?.grouping[group] || {};
    const charges = chargeGroup?.charges;

    if (!charges?.length) {
      return [];
    }

    return charges;
  };

  const getGroupedCharges = (shipmentId, group, chargeId) => {
    const charges = getShipmentGroupCharges(shipmentId, group);

    const groupedType = charges.find((charge) => charge._id === chargeId)?.type;

    if (!groupedType) {
      return [];
    }

    const chargeTypeGroup = charges.filter((charge) => charge?.type === groupedType);

    return chargeTypeGroup.map((charge) => charge._id);
  };

  const isGroupEligible = (shipmentId, group, type) => {
    const charges = getShipmentGroupCharges(shipmentId, group);
    return charges.filter((charge) => charge?.type === type).length > 1;
  };

  const updateSelectedCharges = (shipmentId, group, chargeIds, selected) => {
    setGroupedCharges((currentGroupState) => {
      const currentShipmentGrouping = currentGroupState.find((row) => row.shipmentId === shipmentId);
      if (!currentShipmentGrouping) {
        return currentGroupState;
      }
      const currentShipmentChargeState = currentShipmentGrouping?.grouping[group] || {};
      const shipmentGroupings = currentGroupState.filter((current) => current.shipmentId !== shipmentId);
      const charges = currentShipmentChargeState.charges.map((charge) => {
        if (chargeIds.includes(charge._id)) {
          return mapSelectedCharge(charge, selected);
        }
        return charge;
      });
      const updatedShipmentGrouping = {
        ...currentShipmentGrouping,
        grouping: {
          ...currentShipmentGrouping.grouping,
          [group]: {
            ...currentShipmentChargeState,
            charges,
          }
        }
      };
      return [...shipmentGroupings, updatedShipmentGrouping];
    });
  };

  const updateGroupSelectedCharges = (shipmentId, group, chargesSelected) => {
    const shipmentGroup = groupedCharges.find((group) => group.shipmentId === shipmentId);
    const shipmentGroupings = shipmentGroup?.grouping || {};
    const shipmentGroupCharges = shipmentGroupings[group] || {};
    const shipmentChargeIds = shipmentGroupCharges?.charges.map((charge) => charge._id);
    updateSelectedCharges(shipmentId, group, shipmentChargeIds, chargesSelected);
  };

  const groupCharge = (shipmentId, group, chargeType) => {
    setGroupedCharges((currentGroupState) => {
      const currentShipmentGrouping = currentGroupState.find((row) => row.shipmentId === shipmentId);
      if (!currentShipmentGrouping) {
        return currentGroupState;
      }
      const currentShipmentChargeState = currentShipmentGrouping?.grouping[group] || {};
      let groupedChargeTypes = [];

      if ((currentShipmentChargeState?.groupedChargeTypes || []).includes(chargeType)) {
        groupedChargeTypes = currentShipmentChargeState.groupedChargeTypes.filter((type) => type !== chargeType);
      } else {
        groupedChargeTypes = currentShipmentChargeState.groupedChargeTypes.concat(chargeType)
      }

      if (groupedChargeTypes.some((charge) => charge.selected)) {
        groupedChargeTypes = groupedChargeTypes.map(mapSelectedCharge(charge, true));
      }

      const shipmentGroupings = currentGroupState.filter((current) => current.shipmentId !== shipmentId);
      const updatedShipmentGrouping = {
        ...currentShipmentGrouping,
        grouping: {
          ...currentShipmentGrouping.grouping,
          [group]: {
            ...currentShipmentChargeState,
            groupedChargeTypes,
          }
        }
      };

      const sortedGroupings = [...shipmentGroupings, updatedShipmentGrouping]
      return sortedGroupings;
    });
  };

  const mapSelectedCharge = (charge, isSelected) => {
    return {
      ...charge,
      selected: isSelected,
    };
  }

  const sortCharges = (grouping, shipment) => {
    let sortedCharges = cloneDeep(INVOICE_GROUPINGS[grouping]);
    const charges = (shipment[ledgerType] || []).filter((charge) => !charge.processed);
    if (groupAll) {
      const chargeTypes = charges.reduce((acc, charge) => {
        if (!acc.includes(charge?.type)) {
          acc.push(charge?.type);
        }
        return acc;
      }, []);
      Object.keys(sortedCharges).forEach((group) => {
        sortedCharges[group].groupedChargeTypes = chargeTypes;
      });
    }

    if (grouping === INVOICE_GROUPING_TYPES.TWO_PER_SHIPMENT) {
      const chassisAndPerDiem = charges.filter(charge => charge.type === "chassis" || charge.type === "per_diem").map(charge => mapSelectedCharge(charge, true));
      const allButChassisAndPerDiem = charges.filter(charge => charge.type !== "chassis" && charge.type !== "per_diem").map(charge => mapSelectedCharge(charge, true));
      sortedCharges.CHASSIS_AND_PER_DIEM.charges = chassisAndPerDiem;
      sortedCharges.ALL_BUT_CHASSIS_AND_PER_DIEM.charges = allButChassisAndPerDiem;
    } else if (grouping === INVOICE_GROUPING_TYPES.ONE_PER_SHIPMENT) {
      charges.forEach((charge) => {
        sortedCharges.ALL.charges.push(mapSelectedCharge(charge, true));
      });
    } else {
      charges.forEach((charge) => {
        const mappedCharge = mapSelectedCharge(charge, true);
        if (charge.type === "shipper_cost") {
          sortedCharges.SHIPPER_COST.charges.push(mappedCharge);
        } else if (FUEL_SURCHARGE_TYPES.includes(charge.type)) {
          sortedCharges.FUEL_SURCHARGE.charges.push(mappedCharge);
        } else {
          sortedCharges.ALL_BUT_LINE_HAUL_AND_FUEL_SURCHARGE.charges.push(mappedCharge);
        }
      });
    }

    const shipmentGroups = {
      shipmentId: shipment._id,
      grouping: sortedCharges,
    };
    return shipmentGroups;
  }

  const validateSelectedGroupedCharges = async () => {
    setIsValidatingGroupedCharges(true);
    try {
      const data = mapDataPayload(groupedCharges)
      const selectedCharges = data.map((charge) => {
        let  { SHIPPER_COST, ...remainder } = charge.grouping
        charge.grouping = { ...remainder, LINE_HAUL: SHIPPER_COST };
        return charge;
      })
      if (!selectedCharges?.length) {
        setIsValidatingGroupedCharges(false);
        return;
      }
      const payload = {
        selectedCharges,
      };
      const response = await validateInvoiceChargesByGrouping(orderId, payload);
      handleChargeValidationResponse(response);
      setIsValidatingGroupedCharges(false);
    } catch (e) {
      console.error('Error validating grouped charges: ', e.message);
      showToast(t('ERROR_VALIDATING_GROUPED_CHARGES'));
      setIsValidatingGroupedCharges(false);
    }
  };

  /*
    Take response and for each error, add it to state
  */
  const handleChargeValidationResponse = (response) => {
    try {
      const { data: { results: {
        valid,
        validatedChargeGroupingsToInvoice,
        invalidChargeGroupingsToInvoice,
      } }} = response;
      if (!showValidationAlert && valid !== areValidGroupings) {
        setShowValidationAlert(true);
      };
      if (valid) {
        setAreValidGroupings(valid);
        setValidCharges(validatedChargeGroupingsToInvoice);
      } else {
        setAreValidGroupings(valid);
        setInvalidCharges(invalidChargeGroupingsToInvoice);
      }
    } catch (e) {
      setAreValidGroupings(false);
      throw e;
    }
  };

  const ChargeHeaders = (shipmentId, group) => {
    const shipmentGrouping = (groupedCharges || []).find((group) => group.shipmentId === shipmentId) || {};
    const shipmentCharges = shipmentGrouping?.grouping[group] || {};
    const allChargesSelected = (shipmentCharges?.charges || []).every((charge) => charge?.selected);
    return (
      <thead>
        <tr>
          <th className="w-4">
            <Checkbox checked={allChargesSelected} onChange={(checked) => updateGroupSelectedCharges(shipmentId, group, checked)} />
          </th>
          <th className="w-1/3">{t('NAME')}</th>
          <th className="w-1/6">{t('AMOUNT')}</th>
          <th className="w-1/3">{t('GROUP_RELATED_CHARGES')}?</th>
          <th />
        </tr>
      </thead>
    );
  };

  const handleCloseValidationAlert = () => {
    setShowValidationAlert(!showValidationAlert);
  }

  const renderInvoicingValidationAlert = () => {
    if (!showValidationAlert) {
      return null;
    }
    if (isValidatingGroupedCharges) {
      return (
        <CargomaticAlert
          type="info"
          title={t("AWAITING_CHARGE_VALIDATION")}
          className="mb-4"
          canClose={true}
          onClose={handleCloseValidationAlert}
        >
          <LoadingCircle />
        </CargomaticAlert>
      )
    }

    if (areValidGroupings) {
      return (
        <CargomaticAlert
          type="success"
          title={t("CHARGES_VALID_FOR_INVOICING")}
          className="mb-4"
          canClose={true}
          onClose={handleCloseValidationAlert}
        >
          {t('ALL_CHARGES_VALID_FOR_INVOICING')}
        </CargomaticAlert>
      )
    }

    if (invalidCharges.length) {
      return (
        <CargomaticAlert
        type="error"
        title={t("SOME_CHARGES_INVALID_FOR_INVOICING")}
        className="mb-4"
        canClose={true}
        onClose={handleCloseValidationAlert}
      >
        {mapInvoiceValidationAlertBody()}
      </CargomaticAlert>
      )
    }
  }

  const renderAlert = () => {
    return (
      <CargomaticAlert
        type="info"
        title={t("SHIPPER_INVOICE_GROUPING_CONFIGURATION")}
        className="mb-4"
      >
        {t(invoiceGrouping)}
      </CargomaticAlert>
    )
  }

  const mapInvoiceValidationAlertBody = () => (
    <>
      <div>
        {t('SOME_SELECTED_INVOICE_GROUPINGS_INELIGIBLE_TO_INVOICE')}
      </div>
      <ul className="ml-2 mt-2">
        { invalidCharges?.length && invalidCharges.map(err => {
            const { chargeType } = err;
            return (
              <li key={`invalid-invoice-type-${chargeType.toLowerCase()}`}>
                {chargeType}
              </li>
            );
        })}
      </ul>
    </>
  )

  const renderInvoicingErrorsAlert = () => {
    if (!invoiceErrors?.length) return null;
    return (
      <CargomaticAlert
        type="error"
        title={t("INVOICING_RESPONSE")}
        className="mb-4"
        canClose={true}
      >
        {mapInvoicingAlertBody()}
      </CargomaticAlert>
    )
  }

  const mapInvoicingAlertBody = () => (
    <>
      <div>
        {`${invoiceSuccesses.length} Successful ${invoiceSuccesses.length !== 1 ? 'Invoices' : 'Invoice'}`}
      </div>
      <div>
        {`${invoiceErrors.length} ${invoiceErrors.length !== 1 ? 'Invoices' : 'Invoice'} Failed to Generate`}
      </div>
      <ul>
        {invoiceErrors?.length && invoiceErrors.map(err => {
            const { chargeTypes } = err;
            const typesWithErrors = chargeTypes?.map((type) => type?.toLowerCase()).join(',');
            return (
              <li key={`invoice-error-type-${typesWithErrors}`}>
                {`${t('CHARGE_TYPE')}: ${typesWithErrors}`}
              </li>
            );
        })}
      </ul>
    </>
  )

  const renderShipment = (shipment) => {
    const shipmentGroup = groupedCharges.find(shipmentGroup => shipmentGroup.shipmentId === shipment._id);
    if (shipmentGroup) {
      return (
        <div>
          <PanelSection>
            <h3>{shipment?.shipment_id}</h3>
          </PanelSection>
          <PanelSection>
            {renderGroups(shipmentGroup.grouping, shipmentGroup.shipmentId)}
          </PanelSection>
        </div>
      )
    } else {
      return (
        <div>
          <PanelSection>
            <h3>{shipment?.shipment_id}</h3>
          </PanelSection>
        </div>
      );
    }
  }

  const renderGroups = (groupedCharges, shipmentId) => {
    const groups = Object.keys(groupedCharges);

    return (
      <div>
        {groups.map(group => (
          renderCharges(group, shipmentId, groupedCharges[group])
        ))}
      </div>
    )
  }

  const renderCharges = (group, shipmentId, groupedCharges) => {
    const groupName = group === 'ALL' ? 'ALL_CHARGES' : group;
    return (
      <div>
        <CollapsibleItem text={t(groupName)} defaultOpen>
          <div className="table-wrapper">
            <table className="default-table table-fixed mt-4">
              {ChargeHeaders(shipmentId, group)}
              <tbody>
                {mapChargeRows(shipmentId, group, groupedCharges)}
              </tbody>
              <tfoot className='bg-white'>
                <tr>
                  <td></td>
                  <td>
                    <strong>{t('TOTAL')}</strong>
                  </td>
                  <td>{getTotal(groupedCharges?.charges)}</td>
                  <td></td>
                  <td/>
                </tr>
              </tfoot>
            </table>
          </div>
        </CollapsibleItem>
        <hr className="my-4" />
      </div>
    )
  }

  const getTotal = (groupedCharges) => {
    const total = groupedCharges.reduce((acc, charge) => {
      acc += charge.value;
      return acc;
    }, 0);
    return formatMoney(total);
  }

  const mapChargeRows = (shipmentId, group, shipmentCharges) => {
    const { charges, groupedChargeTypes } = shipmentCharges;

    if (!groupedChargeTypes?.length) {
      return (charges || []).map((charge) => renderSingleCharge(shipmentId, charge, group, false));
    }

    const rows = [];
    const grouped = groupBy(charges, (charge) => charge?.type);

    Object.keys(grouped).forEach((key) => {
      const chargesByGroupType = grouped[key];
      if (!groupedChargeTypes.includes(key)) {
        chargesByGroupType.forEach((charge) => {
          rows.push((renderSingleCharge(shipmentId, charge, group, false)));
        });
      } else {
        const charge = chargesByGroupType[0];
        const total = chargesByGroupType.reduce((typeTotal, currentCharge) => {
          typeTotal += currentCharge.value;
          return typeTotal;
        }, 0);
        const groupedCharge = {
          ...charge,
          value: total,
        };
        rows.push((renderSingleCharge(shipmentId, groupedCharge, group, true)));
      }
    });

    return rows;
  };

  const renderSingleCharge = (shipmentId, charge, group, isGrouped) => {
    let chargeIds = [];

    if (isGrouped) {
      chargeIds = getGroupedCharges(shipmentId, group, charge?._id);
    } else {
      chargeIds = [charge?._id];
    }

    return (
      <tr>
        <td>
          <Checkbox checked={charge?.selected} onChange={(checked) => updateSelectedCharges(shipmentId, group, chargeIds, checked)} />
        </td>
        <td>
          <div className="flex items-center space-x-2">
            <span>{t(sluggify(charge?.type))}</span>
            {charge?.equipment ? <Badge className="green">{getEquipment(charge?.equipment)}</Badge> : null}
          </div>
        </td>
        <td>{formatMoney(charge.value)}</td>
        <td>
          {isGroupEligible(shipmentId, group, charge?.type) && <ToggleSwitch enabled={isGrouped} onChange={() => groupCharge(shipmentId, group, charge?.type)} />}
        </td>
        <td/>
      </tr>
    )
  }

  const getEquipment = (equipmentId) => {
    const equipments = uniqBy((shipments || []).reduce((acc, shipment) => {
      const shipmentEquipment = (shipment.stops || []).reduce((equipment, stop) => {
        equipment = equipment.concat(stop.equipments);
        return equipment;
      }, []);
      acc = acc.concat(shipmentEquipment);
      return acc;
    }, []), (eq) => eq?._id?.toString());

    if (!(equipments && equipments.length)) {
      return null;
    }

    const equipment = equipments.find(equipment => equipment?._id === equipmentId);
    return equipment?.equipment_id;
  }


  const getBody = () => {
    return (
      <LayoutWithSidebar>
        <div className="bg-gray-50 -mx-4 sm:-mx-6">
          <PanelSection>
            {renderAlert()}
            {renderInvoicingValidationAlert()}
            {renderInvoicingErrorsAlert()}
            <div className="flex items-center justify-start mb-2 pr-2 pl-2 py-2">
              <Label labelClassName="block text-base font-medium text-gray-700 font-semibold pr-3">
                {t('GROUP_RELATED_CHARGES')}
              </Label>
              <ToggleSwitch enabled={groupAll} onChange={() => setGroupAll(!groupAll)}/>
            </div>
            <Panel>
              {shipments.map(shipment => (
                renderShipment(shipment)
              ))}
            </Panel>
          </PanelSection>
        </div>
      </LayoutWithSidebar>
    )
  };

  const getFooter = () => {
    return (
      <div className="flex justify-end">
        <Button className="mr-2" variant="secondary" onClick={onClose}>
          {t('CANCEL')}
        </Button>
        {noDeliveryInvoiceButton()}
        <Button
          color="green"
          onClick={() => generateInvoices({noDeliveryInvoice: false})}
          disabled={isInvoiceButtonDisabled()}
        >{t('GENERATE_INVOICE')}</Button>
      </div>
    )
  };

  return (
    <CargomaticModal
      title="Generate Invoice"
      className="w-1/2"
      show={show}
      onHide={onClose}
      body={getBody()}
      footer={getFooter()}
      size="large"
    />
  );
};

const mapDataPayload = (data = []) => {
  return data.map((shipmentData) => {
    const grouping = Object.keys(shipmentData?.grouping)?.reduce((data, key) => {
      const selectedCharges = (shipmentData?.grouping[key].charges || []).filter((charge) => charge?.selected);
      data[key] = {
        charges: selectedCharges?.map((item) => item?._id.toString()),
        groupedChargeTypes: shipmentData?.grouping[key]?.groupedChargeTypes,
      };
      return data;
    }, {});

    return {
      shipmentId: shipmentData?.shipmentId,
      grouping
    };
  });
};